import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";
import { TakeOwnership } from "../../Logic/EmsAPI/Enquiries";
import { LoadMailbox, LoadSearchResults, LoadUnresolved, ReloadActiveList } from "../../Logic/Enquiries/Load";
import { clearDraftIds } from "../../Logic/GenericComms/createNote";
import { setLastCommunication } from "../../Logic/LastTouched/comm";
import { getLastTouched, saveLastTouched } from "../../Logic/LastTouched/misc";
import { GlobalModalContext } from "../../contexts/GlobalModalContext";
import { ChangeEnquiryOwnershipContext } from "../../contexts/changeEnquiryOwnership.context";
import { ChangeEnquiryPriorityContext } from "../../contexts/changeEnquiryPriority.context";
import { ChangeEnquiryStatusContext } from "../../contexts/changeEnquiryStatus.context";
import { ConfirmProvider } from "../../contexts/confirm.text";
import { CreateNoteContext } from "../../contexts/create-note.context";
import { EnquiryGridSelectionContext, gridSelectionProperties } from "../../contexts/enquiry-grid-selection.context";
import { EnquiryPropertiesContext } from "../../contexts/enquiry-properties.context";
import { ExportEnquiriesContext } from "../../contexts/export-enquiries.context";
import { NoActionRequiredEnquiriesContext } from "../../contexts/noActionRequiredEnquiries.context";
import { RemoveEnquiriesContext } from "../../contexts/removeEnquiries.context";
import { ReplaceEnquiryOwnershipContext } from "../../contexts/replaceEnquiryOwnership.context";
import { ErrorFallback } from "../../error-boundary/errorFallback";
import { defaultReset, uploadErrorList } from "../../error-boundary/logerror";
import useInterval from "../../hooks/useInterval";
import { SetLastTouchedEnquiry } from "../../interfaces/enquiryList";
import { iEnquiry } from "../../interfaces/iEnquiry";
import {
    setEnquiriesVisibility,
    setQueuesVisibility,
    setRestrictedView,
    setSearchResultsVisibility,
} from "../../state/siteSlice";
import { RootState, useAppDispatch } from "../../state/store";
import { switchFirstTab } from "../../state/tabRow";
import { getSessionValue, saveSessionValue } from "../../utilities/localStore/calls";
import { clearTempStoreDB, gcMaildrafts } from "../../utilities/localStore/indexDBCalls";
import ChangeEnquiryOwnership from "../ChangeEnquiryOwnershipForm";
import ChangeEnquiryPriority from "../ChangeEnquiryPriority";
import ChangeEnquiryStatus from "../ChangeEnquiryStatus";
import CreateNote from "../CreateNoteForm";
import { EnquiryPropertiesModal } from "../EnquiryProperties/EnquiryPropertiesModal";
import Confirm from "../Generic/ConfirmDialog";
import ExportEnquiries from "../ExportEnquiries";
import GlobalModals, { iGlobalModalsProps } from "../GlobalModals";
import NoActionRequiredEnquiries from "../NoActionRequiredEnquiries/Index";
import RemoveEnquiries from "../RemoveEnquiries/Index";
import FullWidth from "../template/fullWidth/fullWidth";
import RestrictedWidth from "../template/restrictedWidth/restrictedWidth";
import "../template/styles/generic-components.scss";
import "./index.scss";
const initialModalState: iGlobalModalsProps = {};

function Index() {
    const SiteInformation = useSelector((state: RootState) => state.SiteInformation);
    const User = useSelector((state: RootState) => state.UserInformation);
    const permissions = useSelector((state: RootState) => state.permissions);
    const dispatch = useAppDispatch();

    const [showErrorModal, setShowErrorModal] = useState<
        { errorLogId?: string | undefined; message: string } | undefined
    >(undefined);
    const [createNote, setCreateNote] = useState({ shown: false, enquiryId: 0 });
    const [changeEnquiryOwnership, SetChangeEnquiryOwnershipContext] = useState<{
        enquiries: Array<iEnquiry>;
        shown: boolean;
    }>();
    const [replaceEnquiryOwnership, setReplaceEnquiryOwnership] = useState<{
        enquiries: Array<iEnquiry>;
        shown: boolean;
    }>();
    const [enquiryProperties, setEnquiryProperties] = useState({ shown: false, enquiryId: 0 });
    const [removeEnquires, SetRemoveEnquiriesContext] = useState<{ enquiries: Array<iEnquiry>; shown: boolean }>();
    const [noActionRequiredEnquiries, SetNoActionRequiredEnquiriesContext] = useState<{
        enquiries: Array<iEnquiry>;
        shown: boolean;
    }>();

    const [changeEnquiryPriority, SetChangeEnquiryPriorityContext] = useState<{
        enquiries: Array<iEnquiry>;
        shown: boolean;
    }>();
    const [modalState, setModalState] = useState(initialModalState);
    const [selectionProperties, SetEnquiryGridSelectionProperties] = useState<gridSelectionProperties>({
        multiSelect: false,
        selectedEnquiries: [],
    });
    const [changeEnquiryStatus, SetChangeEnquiryStatusContext] = useState<{
        enquiries: Array<iEnquiry>;
        shown: boolean;
    }>();
    const [exportEnquiries, setExportEnquiries] = useState<{
        enquiries: Array<iEnquiry>;
        shown: boolean;
    }>();

    useInterval(() => uploadErrorList(), 10 * 60 * 1000);

    useEffect(() => {
        gcMaildrafts();
        clearTempStoreDB();
        window.addEventListener("resize", HandleResize);
        return () => window.removeEventListener("resize", HandleResize);
    }, []);

    async function HandleResize(e: any) {
        if (SiteInformation.reportsVisible) {
            dispatch(setRestrictedView(e.target.innerWidth < 800));
        } else {
            dispatch(setRestrictedView(e.target.innerWidth < 1066));
        }
    }

    useEffect(() => {
        if (window.innerWidth < 1066 && SiteInformation.restrictedView == false) {
            dispatch(setRestrictedView(true));
        } else if (window.innerWidth > 1065 && SiteInformation.restrictedView == true) {
            dispatch(setRestrictedView(false));
        }
    }, []);

    useEffect(() => {
        (async () => {
            const iLastTouched = getLastTouched();
            if (permissions.Tenant.ViewMyEnquiries) {
                iLastTouched.MyEnquiries.ActiveList = "Unresolved";
                iLastTouched.lastActiveListType = "Unresolved";
                saveLastTouched(iLastTouched);

                await LoadUnresolved();

                dispatch(switchFirstTab("My Enquiries"));
                dispatch(setEnquiriesVisibility());
            } else if (permissions.AnyMailbox.ViewEnquiryList) {
                const mailbox = iLastTouched.Queue.ActiveList;
                iLastTouched.lastActiveListType = mailbox.toString();
                saveLastTouched(iLastTouched);

                await LoadMailbox(mailbox);

                dispatch(switchFirstTab("Mailboxes"));
                dispatch(setQueuesVisibility());
            } else {
                iLastTouched.lastActiveListType = "Results";
                saveLastTouched(iLastTouched);

                await LoadSearchResults();

                dispatch(switchFirstTab("Search Results"));
                dispatch(setSearchResultsVisibility());
            }
        })();
    }, []);

    async function onNoteCreated() {
        setCreateNote({ enquiryId: 0, shown: false });
        setLastCommunication(createNote.enquiryId, 0);
        SetLastTouchedEnquiry(createNote.enquiryId, 0);
        await ReloadActiveList();
    }
    async function onChangeOwnershp() {
        await ReloadActiveList();

        setLastCommunication(0, 0);
        SetChangeEnquiryOwnershipContext({ enquiries: [], shown: false });
        if (selectionProperties.multiSelect) {
            const LastTouched = getLastTouched();
            if (
                User.preferences.general.enquiryList.multiSelectMode == "multi-preview" ||
                LastTouched.lastActiveListType == "Resolved" ||
                LastTouched.lastActiveListType == "Unresolved"
            ) {
                SetEnquiryGridSelectionProperties({ multiSelect: false, selectedEnquiries: [] });
            }
        }
    }

    async function onRemoveEnquiry() {
        await ReloadActiveList();

        if (selectionProperties.multiSelect) {
            SetEnquiryGridSelectionProperties(currentState => {
                currentState = JSON.parse(JSON.stringify(currentState));
                currentState.multiSelect = false;
                currentState.selectedEnquiries = [];
                return currentState;
            });
        }
        SetRemoveEnquiriesContext({ enquiries: [], shown: false });
    }
    async function onNoActionRequiredEnquiry() {
        await ReloadActiveList();

        if (selectionProperties.multiSelect) {
            SetEnquiryGridSelectionProperties(currentState => {
                currentState = JSON.parse(JSON.stringify(currentState));
                currentState.multiSelect = false;
                currentState.selectedEnquiries = [];
                return currentState;
            });
        }
        SetNoActionRequiredEnquiriesContext({ enquiries: [], shown: false });
    }
    async function onChangeEnquiryPriority() {
        await ReloadActiveList();

        SetChangeEnquiryPriorityContext({ enquiries: [], shown: false });
        if (selectionProperties.multiSelect) {
            if (User.preferences.general.enquiryList.multiSelectMode == "multi-preview") {
                SetEnquiryGridSelectionProperties(currentState => {
                    currentState = JSON.parse(JSON.stringify(currentState));
                    currentState.multiSelect = false;
                    currentState.selectedEnquiries = [];
                    return currentState;
                });
            }
        }
    }
    async function onChangeEnquiryStatus() {
        await ReloadActiveList();

        SetChangeEnquiryStatusContext({ enquiries: [], shown: false });
        if (selectionProperties.multiSelect) {
            if (User.preferences.general.enquiryList.multiSelectMode == "multi-preview") {
                SetEnquiryGridSelectionProperties(currentState => {
                    currentState = JSON.parse(JSON.stringify(currentState));
                    currentState.multiSelect = false;
                    currentState.selectedEnquiries = [];
                    return currentState;
                });
            }
        }
    }
    function onPropertiesUpdated() {
        ReloadActiveList();
        setEnquiryProperties({ enquiryId: 0, shown: false });
    }

    useEffect(() => {
        clearDraftIds();
    }, []);

    if (User.userID > 0) {
        if (getSessionValue<boolean>("loggedIn") != true) {
            saveSessionValue("loggedIn", true);
        }
    }

    return (
        <ExportEnquiriesContext.Provider
            value={(enquiries: Array<iEnquiry>) => {
                setExportEnquiries({ enquiries: enquiries, shown: true });
            }}
        >
            <EnquiryPropertiesContext.Provider
                value={(enquiryId: number) => {
                    setEnquiryProperties({ enquiryId, shown: true });
                }}
            >
                <ChangeEnquiryStatusContext.Provider
                    value={(enquiries: Array<iEnquiry>) => {
                        SetChangeEnquiryStatusContext({ enquiries: enquiries, shown: true });
                    }}
                >
                    <ChangeEnquiryPriorityContext.Provider
                        value={(enquiries: Array<iEnquiry>) => {
                            SetChangeEnquiryPriorityContext({ enquiries: enquiries, shown: true });
                        }}
                    >
                        <NoActionRequiredEnquiriesContext.Provider
                            value={(enquiries: Array<iEnquiry>) => {
                                SetNoActionRequiredEnquiriesContext({ enquiries: enquiries, shown: true });
                            }}
                        >
                            <RemoveEnquiriesContext.Provider
                                value={(enquiries: Array<iEnquiry>) => {
                                    SetRemoveEnquiriesContext({ enquiries: enquiries, shown: true });
                                }}
                            >
                                <ReplaceEnquiryOwnershipContext.Provider
                                    value={(enquiries: Array<iEnquiry>) =>
                                        setReplaceEnquiryOwnership({ enquiries: enquiries, shown: true })
                                    }
                                >
                                    <ChangeEnquiryOwnershipContext.Provider
                                        value={(enquiries: Array<iEnquiry>) => {
                                            SetChangeEnquiryOwnershipContext({ enquiries: enquiries, shown: true });
                                        }}
                                    >
                                        <CreateNoteContext.Provider
                                            value={(enquiryId: number) => {
                                                setCreateNote({ enquiryId, shown: true });
                                            }}
                                        >
                                            <GlobalModalContext.Provider
                                                value={newModalState => {
                                                    setModalState(newModalState);
                                                }}
                                            >
                                                <EnquiryGridSelectionContext.Provider
                                                    value={{
                                                        selectionProperties,
                                                        SetEnquiryGridSelectionProperties:
                                                            SetEnquiryGridSelectionProperties,
                                                    }}
                                                >
                                                    <>
                                                        <ErrorBoundary
                                                            FallbackComponent={ErrorFallback}
                                                            onReset={defaultReset}
                                                        >
                                                            {!SiteInformation.restrictedView ? (
                                                                <ConfirmProvider>
                                                                    <FullWidth></FullWidth>
                                                                </ConfirmProvider>
                                                            ) : (
                                                                <ConfirmProvider>
                                                                    <RestrictedWidth></RestrictedWidth>
                                                                </ConfirmProvider>
                                                            )}
                                                        </ErrorBoundary>
                                                        <div
                                                            style={{
                                                                height: "100%",
                                                                width: "100%",
                                                                zIndex: 2147483647,
                                                                position: "absolute",
                                                                top: "0px",
                                                                left: "0px",
                                                                display: "none",
                                                                cursor: "wait",
                                                                justifyContent: "center",
                                                            }}
                                                            id="loader"
                                                        >
                                                            <div className="notice loading">
                                                                Still busy. Please wait...
                                                            </div>
                                                        </div>
                                                        {createNote.shown && (
                                                            <CreateNote
                                                                enquiryId={createNote.enquiryId}
                                                                onCancel={() => {
                                                                    setCreateNote({ enquiryId: 0, shown: false });
                                                                }}
                                                                onCreated={onNoteCreated}
                                                                onError={(error, errorLogId) => {
                                                                    setCreateNote({ enquiryId: 0, shown: false });
                                                                    setShowErrorModal({
                                                                        errorLogId: errorLogId,
                                                                        message: error,
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {changeEnquiryOwnership?.shown && (
                                                            <ChangeEnquiryOwnership
                                                                enquiries={changeEnquiryOwnership.enquiries}
                                                                onCancel={() => {
                                                                    SetChangeEnquiryOwnershipContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                }}
                                                                onCreated={onChangeOwnershp}
                                                                onError={(error, errorLogId) => {
                                                                    SetChangeEnquiryOwnershipContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                    setShowErrorModal({
                                                                        errorLogId: errorLogId,
                                                                        message: error,
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {removeEnquires?.shown && (
                                                            <RemoveEnquiries
                                                                enquiries={removeEnquires.enquiries}
                                                                onCancel={() => {
                                                                    SetRemoveEnquiriesContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                }}
                                                                onCreated={onRemoveEnquiry}
                                                                onError={(error, errorLogId) => {
                                                                    SetRemoveEnquiriesContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                    setShowErrorModal({
                                                                        errorLogId: errorLogId,
                                                                        message: error,
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {noActionRequiredEnquiries?.shown && (
                                                            <NoActionRequiredEnquiries
                                                                enquiries={noActionRequiredEnquiries.enquiries}
                                                                onCancel={() => {
                                                                    SetNoActionRequiredEnquiriesContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                }}
                                                                onCreated={onNoActionRequiredEnquiry}
                                                                onError={(error, errorLogId) => {
                                                                    SetRemoveEnquiriesContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                    setShowErrorModal({
                                                                        errorLogId: errorLogId,
                                                                        message: error,
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {changeEnquiryPriority?.shown && (
                                                            <ChangeEnquiryPriority
                                                                enquiries={changeEnquiryPriority.enquiries}
                                                                onCancel={() => {
                                                                    SetChangeEnquiryPriorityContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                }}
                                                                onCreated={onChangeEnquiryPriority}
                                                                onError={(error, errorLogId) => {
                                                                    SetChangeEnquiryPriorityContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                    setShowErrorModal({
                                                                        errorLogId: errorLogId,
                                                                        message: error,
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {changeEnquiryStatus?.shown && (
                                                            <ChangeEnquiryStatus
                                                                enquiries={changeEnquiryStatus.enquiries}
                                                                onCancel={() => {
                                                                    SetChangeEnquiryStatusContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                }}
                                                                onCreated={onChangeEnquiryStatus}
                                                                onError={(error, errorLogId) => {
                                                                    SetChangeEnquiryStatusContext({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                    setShowErrorModal({
                                                                        errorLogId: errorLogId,
                                                                        message: error,
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {replaceEnquiryOwnership?.shown && (
                                                            <Confirm
                                                                messageType="warn"
                                                                title="Warning"
                                                                onConfirm={async () => {
                                                                    const response = await TakeOwnership(
                                                                        replaceEnquiryOwnership.enquiries.map(
                                                                            x => x.id,
                                                                        ),
                                                                    );
                                                                    setReplaceEnquiryOwnership({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    });
                                                                    if (!response.isOk) {
                                                                        setShowErrorModal({
                                                                            errorLogId: response.errorLogID,
                                                                            message: "Unable to change ownership.",
                                                                        });
                                                                        return;
                                                                    }

                                                                    if (
                                                                        selectionProperties.multiSelect &&
                                                                        User.preferences.general.enquiryList
                                                                            .multiSelectMode == "multi-preview"
                                                                    ) {
                                                                        SetEnquiryGridSelectionProperties({
                                                                            multiSelect: false,
                                                                            selectedEnquiries: [],
                                                                        });
                                                                    }

                                                                    await ReloadActiveList();
                                                                }}
                                                                onCancel={() =>
                                                                    setReplaceEnquiryOwnership({
                                                                        enquiries: [],
                                                                        shown: false,
                                                                    })
                                                                }
                                                            >
                                                                <>
                                                                    {(replaceEnquiryOwnership.enquiries.length == 1
                                                                        ? `This enquiry is already owned by ${
                                                                              replaceEnquiryOwnership.enquiries[0]
                                                                                  ?.owners[0].firstname
                                                                          } ${
                                                                              replaceEnquiryOwnership.enquiries[0]
                                                                                  ?.owners[0].lastname
                                                                          }. `
                                                                        : "Enquiries you have selected are owned by someone else.") +
                                                                        "Are you sure you want the current owner(s) to be replaced with you as the new owner?"}
                                                                </>
                                                            </Confirm>
                                                        )}
                                                        {enquiryProperties.shown && (
                                                            <EnquiryPropertiesModal
                                                                enquiryId={enquiryProperties.enquiryId}
                                                                onCancel={() => {
                                                                    setEnquiryProperties({
                                                                        enquiryId: 0,
                                                                        shown: false,
                                                                    });
                                                                }}
                                                                onCreated={onPropertiesUpdated}
                                                                onError={(error, errorLogId) => {
                                                                    setEnquiryProperties({
                                                                        enquiryId: 0,
                                                                        shown: false,
                                                                    });
                                                                    setShowErrorModal({
                                                                        errorLogId: errorLogId,
                                                                        message: error,
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {showErrorModal && (
                                                            <Confirm
                                                                messageType="err"
                                                                title="Error!"
                                                                onConfirm={() => {
                                                                    setShowErrorModal(undefined);
                                                                    ReloadActiveList();
                                                                }}
                                                            >
                                                                {
                                                                    <>
                                                                        <p>{showErrorModal.message}</p>
                                                                        <p>
                                                                            Please try again or contact support if the
                                                                            issue persists.
                                                                        </p>
                                                                        {showErrorModal.errorLogId && (
                                                                            <p>
                                                                                Reference Number:{" "}
                                                                                <b>{showErrorModal.errorLogId}</b>
                                                                            </p>
                                                                        )}
                                                                    </>
                                                                }
                                                            </Confirm>
                                                        )}

                                                        {exportEnquiries?.shown && (
                                                            <ExportEnquiries
                                                                enquiries={exportEnquiries.enquiries}
                                                                mailboxes={User.mailboxes}
                                                                onClose={() => {
                                                                    setExportEnquiries({ enquiries: [], shown: false });
                                                                }}
                                                            />
                                                        )}
                                                        <GlobalModals {...modalState} />
                                                    </>
                                                </EnquiryGridSelectionContext.Provider>
                                            </GlobalModalContext.Provider>
                                        </CreateNoteContext.Provider>
                                    </ChangeEnquiryOwnershipContext.Provider>
                                </ReplaceEnquiryOwnershipContext.Provider>
                            </RemoveEnquiriesContext.Provider>
                        </NoActionRequiredEnquiriesContext.Provider>
                    </ChangeEnquiryPriorityContext.Provider>
                </ChangeEnquiryStatusContext.Provider>
            </EnquiryPropertiesContext.Provider>
        </ExportEnquiriesContext.Provider>
    );
}

export default Index;
